import { ReactComponent as IconBack } from 'assets/icons/icon-arrow-left.svg';
import { ReactComponent as IconLink } from 'assets/icons/icon-arrow-more.svg';
import { upperFirst } from 'lodash';
import React, { MouseEvent, PropsWithChildren, forwardRef } from 'react';

import TouchableLink from '@skippay/_current/components/TouchableLink';
import { Touchable } from '@skippay/core/@types/touchable';
import { ReactComponent as IconExternalLink } from '@skippay/core/assets/icons/External-link.svg';
import { BreadcrumbCategory, captureBreadcrumb } from '@skippay/core/global/errorTracking';
import { size } from '@skippay/core/styles/spacing';
import { createHrefWithRedirect } from '@skippay/core/utils/routeUtils';

import { LinkVariant } from './@types/linkVariant';
import { SIcon, SLink } from './index.styled';

export type Props = Touchable & {
	analyticsId: string;
	back?: boolean;
	breakout?: boolean;
	/**
	 * Renders just styled link without click actions, use rarely e.g. when click
	 * action is handled by some parent element
	 */
	isDead?: boolean;
	hideChildren?: boolean;
	iconLeft?: {
		icon: JSX.Element;
		size?: [number, number];
	};
	iconRight?:
		| {
				icon: JSX.Element;
				size?: [number, number];
		  }
		| 'arrow';
	isDisabled?: boolean;
	isExternal?: boolean;
	withExternalIcon?: boolean;
	textDecoration?: Array<'underline' | 'none'>; // text-decoration [default, hover&focus]
	testId: string;
	title?: string;
	variant?: LinkVariant;
	width?: 'auto' | 'full' | string;
	withImage?: boolean;
	className?: string;
};

const Link = forwardRef(
	(props: PropsWithChildren<Props>, ref: React.RefObject<HTMLAnchorElement>): JSX.Element => {
		const {
			analyticsId,
			back,
			isDead,
			hideChildren,
			iconLeft,
			iconRight,
			isDisabled,
			isExternal,
			onClick,
			openInPopupBrowser,
			shareUserSession,
			shouldClosePopupBrowser,
			testId,
			to,
			variant = 'primary',
			width = 'auto',
			onPopupBrowserClose,
			withExternalIcon,
			...other
		} = props;
		const externalLink: boolean =
			(Boolean(isExternal) && !process.env.MOB_APP_BUILD) ||
			(Boolean(isExternal) && !openInPopupBrowser && Boolean(process.env.MOB_APP_BUILD));
		const dataAnalyticsParam = analyticsId ? `link${upperFirst(analyticsId)}` : undefined;
		const dataTestId = `link${upperFirst(testId)}`;

		const handleClickWrapper = (ev: MouseEvent<HTMLAnchorElement>) => {
			captureBreadcrumb(BreadcrumbCategory.linkClick, `Link clicked: ${testId}`, {
				testId,
				analyticsId: analyticsId || 'none',
				variant,
			});

			onClick?.(ev);
		};

		const renderChildren = () => {
			if (externalLink && withExternalIcon) {
				return (
					<span className="inline-flex flex-row items-center gap-2">
						{!hideChildren && props.children}
						<IconExternalLink className="h-6 w-6" />
					</span>
				);
			}
			return (
				<>
					{(iconLeft || back) && (
						<SIcon
							isLeft
							size={back ? [7, 7] : iconLeft?.size}
							{...(back && { css: { marginRight: size(2) } })}
						>
							{iconLeft?.icon || <IconBack />}
						</SIcon>
					)}

					{!hideChildren && props.children}

					{iconRight && (
						<SIcon size={iconRight !== 'arrow' ? iconRight.size : [5, 4]}>
							{iconRight !== 'arrow' ? iconRight.icon : <IconLink />}
						</SIcon>
					)}
				</>
			);
		};

		if (!(to || onClick) && !isDead) {
			throw new Error(`Component must be provided with prop 'to' or 'onClick'`);
		}

		const component = externalLink ? 'a' : isDead ? 'div' : to ? TouchableLink : 'button';

		const href = externalLink ? createHrefWithRedirect(to) : undefined;

		return (
			<SLink
				target={externalLink ? '_blank' : undefined}
				{...other}
				as={component}
				to={to}
				href={href}
				onClick={handleClickWrapper}
				data-test-id={dataTestId}
				data-analytics-param={dataAnalyticsParam}
				variant={variant}
				width={width}
				ref={ref}
				{...(component === 'button' && { type: 'button' })}
				{...((isDisabled || isDead) && { 'aria-disabled': 'true' })}
				// html links do not supprt disabled attribute, so disabled is set for buttons only
				{...(isDisabled && component === 'button' && { disabled: true })}
				{...(process.env.MOB_APP_BUILD && openInPopupBrowser
					? { onPopupBrowserClose, openInPopupBrowser, shareUserSession }
					: { shouldClosePopupBrowser })}
			>
				{renderChildren()}
			</SLink>
		);
	}
);

export default Link;
